import React from "react";
import PropTypes from "prop-types";
import OutsideClickHandler from "react-outside-click-handler";

import "./report-collapsible.scss";
import { Box } from "../../boxes";
import { Grid, GridItem } from "../../grids";
import { Icon } from "../../icons";
import { useWindowDimensions } from "../../../utils";
import { useState } from "react";

const ContentMenu = ({ setIsContentMenuOpen, options, componentId }) => {
  const renderOptions = () => {
    return options.map((option, index) => {
      return (
        <div
          key={index}
          className={[
            "report-collapsible__content-menu__button",
            option.color &&
              `report-collapsible__content-menu__button--${option.color}`,
          ].join(" ")}
          onClick={() => {
            setIsContentMenuOpen(false);
            option.onClick(componentId);
          }}
        >
          <p className="text">{option.label}</p>
        </div>
      );
    });
  };

  return (
    <Box classes="report-collapsible__content-menu">{renderOptions()}</Box>
  );
};

ContentMenu.propTypes = {
  setIsContentMenuOpen: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ).isRequired,
  componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * ReportCollapsible
 *
 * Collapsible component used for reporting
 *
 * @return {jsx}
 */
export const ReportCollapsible = ({
  headingItems,
  headingText,
  contentHeading,
  contentText,
  contentMenuOptions,
  componentId = "",
  children,
  canCollapse = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { width } = useWindowDimensions();
  const [isContentMenuOpen, setIsContentMenuOpen] = useState(false);

  return (
    <Box
      classes={`report-collapsible ${
        isOpen ? "report-collapsible--open" : ""
      } ${!canCollapse ? "report-collapsible--no-pointer" : ""}`}
      boxShadow={2}
      borderSize="sm"
    >
      <Grid
        classes="report-collapsible__grid"
        onClick={() => {
          if (canCollapse) {
            setIsOpen(!isOpen);
          }
        }}
      >
        {headingItems.map((item, index) => {
          return (
            <div
              className={`report-collapsible__grid__item report-collapsible__grid__item--${
                headingItems.length === 4
                  ? "sm"
                  : headingItems.length === 2
                  ? index === 1
                    ? "xl"
                    : "md"
                  : headingItems.length === 1
                  ? "xxl"
                  : index === 1
                  ? "md"
                  : "sm"
              }
              `}
              key={index}
            >
              {item}
              {((((width < 900 && index === 0) ||
                (width >= 900 &&
                  index === headingItems.length - 1 &&
                  headingItems.length === 2)) &&
                canCollapse) ||
                (canCollapse && headingItems.length === 1)) && (
                <Icon
                  name="arrow-chevron-down"
                  classes="report-collapsible__grid__arrow"
                  color="#20809E"
                />
              )}
            </div>
          );
        })}
        {width >= 900 && headingItems.length === 3 ? (
          <div className="report-collapsible__grid__item report-collapsible__grid__item--xs">
            <Icon
              name="arrow-chevron-down"
              classes="report-collapsible__grid__arrow"
              color="#20809E"
            />
          </div>
        ) : null}
        {headingText ? (
          <div className="report-collapsible__grid__item report-collapsible__grid__item--xxl">
            <div className="report-collapsible__info-container">
              <div>
                <Icon name="info" size="sm" />
              </div>
              <p className="small-text">{headingText}</p>
            </div>
          </div>
        ) : null}
      </Grid>
      {isOpen && (
        <Grid classes="report-collapsible__content-grid">
          <GridItem md={8} lg={12}>
            <div className="report-collapsible__content-heading-container">
              <h4 className="report-collapsible__content-grid__heading">
                {contentHeading}
              </h4>
              {contentMenuOptions && (
                <Icon
                  name="three-dots-vertical"
                  color={"#20809E"}
                  onClick={() => setIsContentMenuOpen(!isContentMenuOpen)}
                  classes="report-collapsible__content-heading-container__three-dots"
                />
              )}
            </div>
          </GridItem>
          <GridItem md={8} lg={12}>
            <p className="text">{contentText}</p>
          </GridItem>
          {children && (
            <GridItem md={8} lg={12}>
              {children}
            </GridItem>
          )}
        </Grid>
      )}
      {contentMenuOptions && isContentMenuOpen && (
        <OutsideClickHandler onOutsideClick={() => setIsContentMenuOpen(false)}>
          <ContentMenu
            options={contentMenuOptions}
            setIsContentMenuOpen={setIsContentMenuOpen}
            componentId={componentId}
          />
        </OutsideClickHandler>
      )}
    </Box>
  );
};

ReportCollapsible.propTypes = {
  /**
   * Items that will be displayed in the main part of the component
   */
  headingItems: PropTypes.arrayOf(PropTypes.node).isRequired,

  /**
   * Optional info text displayed under the heading items
   */
  headingText: PropTypes.string,

  /**
   * The heading of the collapsible part of the component
   */
  contentHeading: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,

  /**
   * Content displayed in the collapsible part of the component
   */
  contentText: PropTypes.string.isRequired,

  /**
   * Optional menu options for the content section
   */
  contentMenuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),

  /**
   * Optional id passed back from menu actions
   */
  componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * Optional children rendered inside the collapsible content
   */
  children: PropTypes.node,

  /**
   * Whether the collapsible can be toggled open/closed
   */
  canCollapse: PropTypes.bool,
};
